import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHistory, useParams } from 'react-router';
import DashboardWrapper from '../../../Shared/DashboardWrapper';
import { SkeletonText } from '../../../Shared/Skeletons';
import { formatDate } from '../../MainDashboard/Common/formatters';
import { getExternalLink } from '../../../../lib/api/external-link';
import { getEnquiryFunctionalityFeatureFlag } from '../../../../lib/api/enquiry-functionality';
import { useEnquiryDetail } from '../../../../lib/hooks/useEnquiryDetail';
import EnquiryStatusPill from '../EnquiryStatusPill';
import {
    getBreadcrumb,
    getOriginContent,
    getTimelineNodes,
    isValidEnquiryReference,
} from './enquiryDetailDisplay';

export default function EnquiryDetail() {
    const { reference } = useParams();
    const [helpLink, setHelpLink] = useState('');
    const history = useHistory();

    const validReference = isValidEnquiryReference(reference);
    const { data, loading, error, reload } = useEnquiryDetail(validReference ? reference : null);

    useEffect(() => {
        getEnquiryFunctionalityFeatureFlag()
            .then((resp) => {
                if (resp?.data?.data !== true) history.push('/dashboard/main');
            })
            .catch(() => {
                history.push('/dashboard/main');
            });
    }, [history]);

    useEffect(() => {
        getExternalLink()
            .then((resp) => setHelpLink(resp.data.faqLink))
            .catch(() => setHelpLink(''));
    }, []);

    const detail = data?.isSuccess === false ? null : data?.data || null;
    const notFound = !validReference || error?.response?.status === 404;
    const loadFailed = !notFound && !loading && (Boolean(error) || data?.isSuccess === false || (data && !detail));

    const timelineNodes = detail ? getTimelineNodes(detail) : [];
    const originContent = detail ? getOriginContent(detail) : null;
    const breadcrumb = detail ? getBreadcrumb(detail) : null;

    return (
        <DashboardWrapper helpLink={helpLink}>
            <div className="main-dashboard" id="enquiry-detail-page">
                <Link to="/enquiries" className="enquiry-detail__back" id="enquiry-detail-back">
                    <span aria-hidden="true" className="overview-back__icon">&lt;</span>
                    Back to your enquiries
                </Link>

                {loading && (
                    <div className="enquiry-detail__card" id="enquiry-detail-loading">
                        <SkeletonText className="w-1/3" />
                        <SkeletonText className="w-2/3" />
                        <SkeletonText className="w-full" />
                        <SkeletonText className="w-full" />
                    </div>
                )}

                {!loading && notFound && (
                    <div className="enquiries__empty" id="enquiry-detail-not-found">
                        <p>We couldn't find that enquiry.</p>
                        <Link to="/enquiries" className="enquiries-tile__btn" id="enquiry-detail-not-found-back">
                            Back to your enquiries
                        </Link>
                    </div>
                )}

                {loadFailed && (
                    <div className="enquiries__empty" id="enquiry-detail-error">
                        There was an error loading your enquiry, please click{' '}
                        <span
                            onClick={reload}
                            className="text-everflow-magenta underline cursor-pointer"
                        >
                            here
                        </span>{' '}
                        to try again.
                    </div>
                )}

                {!loading && !notFound && !loadFailed && detail && (
                    <>
                        <section className="enquiry-detail__heading" id="enquiry-detail-heading">
                            <h1 id="enquiry-detail-title">Enquiry {detail.enquiryReference}</h1>
                            <EnquiryStatusPill id="enquiry-detail-status" status={detail.status} />
                        </section>

                        <p className="enquiry-detail__hint" id="enquiry-detail-hint">
                            {breadcrumb ? `${breadcrumb} · ` : ''}
                            raised {formatDate(detail.createdOn)}
                        </p>

                        <section className="enquiry-detail__timeline" id="enquiry-detail-timeline">
                            <h2 className="enquiry-detail__section-title" id="enquiry-detail-timeline-title">Progress</h2>
                            <div className="enquiry-detail__nodes">
                                {timelineNodes.map((node) => (
                                    <div
                                        className={`enquiry-detail__node enquiry-detail__node--${node.state}`}
                                        id={`enquiry-detail-timeline-${node.key}`}
                                        key={node.key}
                                    >
                                        <span className="enquiry-detail__node-dot" aria-hidden="true" />
                                        <span className="enquiry-detail__node-label" id={`enquiry-detail-timeline-${node.key}-label`}>
                                            {node.label}
                                        </span>
                                        <span className="enquiry-detail__node-date" id={`enquiry-detail-timeline-${node.key}-date`}>
                                            {node.dateText}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {originContent?.originNote && (
                            <p className="enquiry-detail__origin-note" id="enquiry-detail-origin-note">
                                {originContent.originNote}
                            </p>
                        )}

                        {originContent?.showSubmittedSection && (
                            <section className="enquiry-detail__submitted" id="enquiry-detail-submitted">
                                <h2 className="enquiry-detail__section-title" id="enquiry-detail-submitted-title">
                                    {originContent.sectionTitle}
                                </h2>
                                {detail.origin === 'Portal' && detail.optionName && (
                                    <p className="enquiry-detail__submitted-option" id="enquiry-detail-submitted-option">
                                        You asked about: {detail.optionName}
                                    </p>
                                )}
                                <p className="enquiry-detail__submitted-text" id="enquiry-detail-submitted-text">
                                    {detail.submittedText}
                                </p>
                            </section>
                        )}
                    </>
                )}
            </div>
        </DashboardWrapper>
    );
}
